import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PanelLeftClose,
  PanelLeftOpen,
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  Terminal,
  Shield,
  ShieldCheck,
  Globe,
  Settings,
  LogOut,
  Home,
  ChevronsUpDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Logo from './ui/Logo';
import ThemeControl from './ui/ThemeControl';
import { useToast } from './ui/Toast';

function initialsOf(user) {
  const source = (user?.displayName || user?.email || '?').trim();
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : source.slice(0, 2);
  return letters.toUpperCase();
}

function NavItem({ icon: Icon, label, active, onClick, collapsed, count = 0, countClass = 'text-pp-faint', live = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? label : undefined}
      className={`group relative flex h-8 w-full items-center gap-2.5 rounded-lg text-[13px] font-medium
                  transition-colors duration-150
                  ${collapsed ? 'justify-center px-0' : 'px-2.5'}
                  ${active
                    ? 'bg-pp-text/[0.07] text-pp-text'
                    : 'text-pp-muted hover:bg-pp-text/[0.04] hover:text-pp-text'}`}
    >
      <span className="relative flex-shrink-0">
        <Icon size={16} strokeWidth={active ? 2.25 : 2} className={active ? 'text-pp-accent' : ''} />
        {live && collapsed && (
          <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-pp-get ring-2 ring-pp-bg" />
        )}
      </span>
      {!collapsed && <span className="min-w-0 flex-1 truncate text-left">{label}</span>}
      {!collapsed && live && (
        <span className="flex items-center gap-1.5 text-2xs font-medium text-pp-get">
          <span className="pp-dot !h-1.5 !w-1.5 animate-status-glow bg-pp-get" />
          Live
        </span>
      )}
      {!collapsed && !live && count > 0 && (
        <span className={`text-2xs tabular-nums ${countClass}`}>{count > 999 ? '999+' : count}</span>
      )}
    </button>
  );
}

function NavSection({ title, collapsed, children }) {
  return (
    <div>
      {collapsed
        ? <div className="mx-auto mb-2 mt-1 h-px w-6 bg-pp-border" aria-hidden="true" />
        : <p className="mb-1 px-2.5 text-2xs font-semibold text-pp-faint">{title}</p>}
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// One line of the status block: is this link working, and if not, what to do.
function StatusLine({ ok, label, value, hint, collapsed }) {
  const dot = <span className={`pp-dot ${ok ? 'bg-pp-get' : 'animate-pulse bg-pp-put'}`} />;
  if (collapsed) {
    return <span className="flex h-5 items-center justify-center" title={`${label}: ${value}. ${hint}`}>{dot}</span>;
  }
  return (
    <div className="flex items-center gap-2 text-xs" title={hint}>
      {dot}
      <span className="text-pp-muted">{label}</span>
      <span className={`ml-auto font-medium ${ok ? 'text-pp-text' : 'text-pp-put'}`}>{value}</span>
    </div>
  );
}

function Sidebar({
  collapsed,
  onToggle,
  isConnected,
  extensionConnected = false,
  requestCount,
  activeView,
  onViewChange,
  tunnelActive,
  consoleCount = 0,
  errorCount = 0,
  successCount = 0,
}) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the account menu on an outside click or Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logout();
    } catch (err) {
      // The server refused to end the session (audit F2): saying so beats pretending
      toast.error(err.message || 'Could not log out. Try again.');
    }
  };

  const go = (view) => () => onViewChange(view);

  return (
    <aside
      className={`flex flex-shrink-0 flex-col border-r border-pp-border bg-pp-bg
                  transition-[width] duration-200 ease-out-soft ${collapsed ? 'w-[60px]' : 'w-[232px]'}`}
    >
      {/* Brand */}
      <div className={`flex h-[52px] flex-shrink-0 items-center ${collapsed ? 'justify-center' : 'justify-between pl-3.5 pr-2'}`}>
        {!collapsed && (
          <Link to="/" className="flex min-w-0 items-center gap-2.5 rounded-md transition-opacity hover:opacity-80" title="Go to the home page">
            <Logo size={26} />
            <span className="truncate text-[15px] font-semibold tracking-tight text-pp-text">Intercept API</span>
          </Link>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="pp-icon-btn"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-4 overflow-y-auto px-2 pb-3 pt-2" aria-label="Dashboard">
        <NavSection title="Traffic" collapsed={collapsed}>
          <NavItem icon={Activity} label="Requests" active={activeView === 'requests'} onClick={go('requests')}
                   collapsed={collapsed} count={requestCount} />
          {/* Error Analyzer — mirrors the extension tab: 4xx/5xx/network errors */}
          <NavItem icon={AlertTriangle} label="Error Analyzer" active={activeView === 'errors'} onClick={go('errors')}
                   collapsed={collapsed} count={errorCount} countClass="text-pp-delete" />
          {/* Traffic Explorer — mirrors the extension tab: 2xx/3xx responses */}
          <NavItem icon={ArrowLeftRight} label="Traffic Explorer" active={activeView === 'traffic'} onClick={go('traffic')}
                   collapsed={collapsed} count={successCount} />
        </NavSection>

        <NavSection title="Tools" collapsed={collapsed}>
          <NavItem icon={Terminal} label="Console" active={activeView === 'console'} onClick={go('console')}
                   collapsed={collapsed} count={consoleCount} />
          <NavItem icon={Shield} label="Mock rules" active={activeView === 'mocks'} onClick={go('mocks')}
                   collapsed={collapsed} />
          <NavItem icon={Globe} label="Tunnel" active={activeView === 'tunnel'} onClick={go('tunnel')}
                   collapsed={collapsed} live={tunnelActive} />
        </NavSection>

        <NavSection title="Account" collapsed={collapsed}>
          <NavItem icon={Settings} label="Settings" active={activeView === 'settings'} onClick={go('settings')}
                   collapsed={collapsed} />
          {/* Admin — visible only for admin users */}
          {user?.role === 'admin' && (
            <NavItem icon={ShieldCheck} label="Admin" active={activeView === 'admin'} onClick={go('admin')}
                     collapsed={collapsed} />
          )}
        </NavSection>
      </nav>

      {/* Status: the two links the dashboard depends on */}
      <div className={`flex-shrink-0 border-t border-pp-border ${collapsed ? 'space-y-1 px-2 py-2.5' : 'space-y-1.5 px-4 py-3'}`}>
        <StatusLine
          collapsed={collapsed}
          ok={isConnected}
          label="Live updates"
          value={isConnected ? 'On' : 'Reconnecting…'}
          hint={isConnected
            ? 'New requests appear here as they happen.'
            : 'Trying to reach the server. The list updates again once the connection is back.'}
        />
        <StatusLine
          collapsed={collapsed}
          ok={extensionConnected}
          label="Extension"
          value={extensionConnected ? 'Connected' : 'Not connected'}
          hint={extensionConnected
            ? 'The Chrome extension is signed in and sending requests.'
            : 'Open the Intercept API extension in Chrome and sign in to start capturing.'}
        />
      </div>

      {/* Account */}
      {user && (
        <div ref={menuRef} className="relative flex-shrink-0 border-t border-pp-border p-2">
          {menuOpen && (
            <div className={`pp-menu absolute bottom-full mb-1 ${collapsed ? 'left-2 w-60' : 'left-2 right-2'}`} role="menu">
              <div className="px-2.5 pb-2 pt-1.5">
                {user.displayName && <p className="truncate text-[13px] font-semibold text-pp-text">{user.displayName}</p>}
                <p className="truncate text-xs text-pp-muted">{user.email}</p>
              </div>
              <div className="px-2 pb-2">
                <ThemeControl size="sm" fullWidth />
              </div>
              <div className="my-1 h-px bg-pp-border" />
              <Link to="/" className="pp-menu-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                <Home size={14} className="text-pp-muted" />
                Home page
              </Link>
              <button type="button" className="pp-menu-item" role="menuitem"
                      onClick={() => { setMenuOpen(false); onViewChange('settings'); }}>
                <Settings size={14} className="text-pp-muted" />
                Settings
              </button>
              <div className="my-1 h-px bg-pp-border" />
              <button type="button" className="pp-menu-item !text-pp-delete" role="menuitem" onClick={handleLogout}>
                <LogOut size={14} />
                Log out
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={collapsed ? user.email : 'Account and appearance'}
            className={`flex w-full items-center gap-2.5 rounded-lg transition-colors duration-150 hover:bg-pp-text/[0.05]
                        ${collapsed ? 'justify-center p-1.5' : 'px-2 py-1.5'}`}
          >
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-pp-accent/[0.14] text-2xs font-semibold text-pp-accent">
              {initialsOf(user)}
            </span>
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-[13px] font-medium leading-tight text-pp-text">
                    {user.displayName || user.email}
                  </span>
                  {user.displayName && (
                    <span className="block truncate text-2xs leading-tight text-pp-muted">{user.email}</span>
                  )}
                </span>
                <ChevronsUpDown size={14} className="flex-shrink-0 text-pp-faint" />
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
